import React, { useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import { Clock, ShoppingBag, Truck } from "lucide-react-native";
import SettingsItem from "../common/settings-item";
import StyledText from "../../../common/ui/styled-text";
import { Input } from "../../../common/ui/input.native";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import { usePostHog } from "posthog-js/react";

const DefaultPrepTimes: React.FC = () => {
  const {
    settings,
    handleDeliveryPrepTimeChange,
    handlePickupPrepTimeChange,
    isTestCallActive,
    validationErrors,
  } = useSettingsData();
  const posthog = usePostHog();

  const orderingConfig = settings.orderingConfig;
  const isDisabled =
    isTestCallActive || settings.orderingConfig?.dynamicPrepTime === true;

  const isDeliveryEnabled = !!orderingConfig?.deliveryModuleConfig?.enabled;
  const isPickupEnabled =
    !!orderingConfig?.pickupModuleConfig?.enabled || !isDeliveryEnabled;

  const pickupPrep = settings.pickupPrepTime ?? "30";
  const deliveryPrep = settings.deliveryPrepTime ?? "30";

  const handlePickupChange = useCallback(
    (val: string) => {
      posthog.capture("prep_time_pickup_changed", {
        time: val,
      });
      handlePickupPrepTimeChange(val);
      if (isDeliveryEnabled && deliveryPrep === "")
        handleDeliveryPrepTimeChange(val);
    },
    [deliveryPrep, handlePickupPrepTimeChange]
  );

  const handleDeliveryChange = useCallback(
    (val: string) => {
      posthog.capture("prep_time_delivery_changed", {
        time: val,
      });
      handleDeliveryPrepTimeChange(val);
      // If pickup empty, mirror for better UX
      if (isPickupEnabled && pickupPrep === "") {
        handlePickupPrepTimeChange(val);
      }
    },
    [pickupPrep, handleDeliveryPrepTimeChange, handlePickupPrepTimeChange]
  );

  return (
    <SettingsItem
      title="Default prep times"
      description="The average time needed to prepare an order."
      icon={Clock}
      disabled={isDisabled}
    >
      <View className="flex-col sm:flex-row gap-4 sm:gap-6">
        {isPickupEnabled && (
          <View
            className={`flex-1 border rounded-lg p-4 shadow-xs ${
              isPickupEnabled && !isDisabled
                ? "border-gray-200 bg-white"
                : "border-gray-100 bg-gray-100 opacity-50"
            }`}
            pointerEvents={isPickupEnabled ? "auto" : "none"}
          >
            <View className="flex-row items-center gap-2 mb-3">
              <ShoppingBag size={16} color="#16a34a" />
              <StyledText className="font-medium">Pickup</StyledText>
            </View>
            <View className="flex-row items-center gap-2">
              <Input
                value={pickupPrep}
                onChangeText={handlePickupChange}
                maxLength={5}
                keyboardType="number-pad"
                placeholder="0"
                placeholderTextColor="#e5e7eb"
                className={`w-20 h-9 text-center border rounded-md px-2 text-base ${
                  validationErrors.pickupPrepTime
                    ? "border-red-500"
                    : "border-gray-200"
                } ${!isPickupEnabled ? "opacity-60" : ""}`}
                editable={!isTestCallActive && isPickupEnabled}
              />
              <StyledText className="text-sm">minutes</StyledText>
            </View>
          </View>
        )}
        {isDeliveryEnabled && (
          <View
            className={`flex-1 border rounded-lg p-4 shadow-xs ${
              isDeliveryEnabled && !isDisabled
                ? "border-gray-200 bg-white"
                : "border-gray-100 bg-gray-100 opacity-50"
            }`}
            pointerEvents={isDeliveryEnabled ? "auto" : "none"}
          >
            <View className="flex-row items-center gap-2 mb-3">
              <Truck size={16} color="#1d4ed8" />
              <StyledText className="font-medium">Delivery</StyledText>
            </View>
            <View className="flex-row items-center gap-2">
              <Input
                value={deliveryPrep}
                onChangeText={handleDeliveryChange}
                maxLength={5}
                keyboardType="number-pad"
                placeholder="0"
                placeholderTextColor="#e5e7eb"
                className={`w-20 h-9 text-center border rounded-md px-2 text-base ${
                  validationErrors.deliveryPrepTime
                    ? "border-red-500"
                    : "border-gray-200"
                } ${!isDeliveryEnabled ? "opacity-60" : ""}`}
                editable={!isTestCallActive && isDeliveryEnabled}
              />
              <StyledText className="text-sm">minutes</StyledText>
            </View>
          </View>
        )}
      </View>

      {validationErrors.pickupPrepTime && (
        <StyledText className="text-red-500 text-sm mt-3 ml-0.5">
          {validationErrors.pickupPrepTime}
        </StyledText>
      )}
      {validationErrors.deliveryPrepTime && (
        <StyledText className="text-red-500 text-sm mt-3 ml-0.5">
          {validationErrors.deliveryPrepTime}
        </StyledText>
      )}
    </SettingsItem>
  );
};

export default DefaultPrepTimes;
